<template>
    <span
        ref="element"
        :class="(valueInMilliseconds ?? 0) < 0 ? props.negativeClasses : ''"
    ></span>
</template>
<script lang="ts" setup>
import { onMounted, ref, onUnmounted, watch } from 'vue';

/**
 * This will accept a time in either date or milliseconds, and countdown by a resolution of seconds or tenth of seconds.
 * Will countdown a given time, and emit a result when it reaces 0
 */

const props = withDefaults(
    defineProps<{
        /**
         * The time to countdown to. This can be a date or a number of milliseconds. If it is a date, it will countdown to that date. If it is a number, it will countdown from that number of milliseconds.
         */
        value: Date | number | null;

        /**
         * This is the resolution of the countdown. If it is 'seconds', it will update every second. If it is 'tenths', it will update every tenth of a second.
         */
        resolution?: 'seconds' | 'tenths';

        /**
         * If we want to also show the negative time after reaching 0. If this is false, the countdown will stop at 0 and emit the event. If this is true,
         */
        showNegative?: boolean;

        /**
         * This will remove the empty parts of the time, i.e hours == 0 then it will not show the hours part, etc. So if we have 0 hours, 5 minutes and 10 seconds, it will show 05:10 instead of 00:05:10. If this is false, it will always show all parts of the time.
         */
        hideEmptyParts?: boolean;

        /**
         * Classes to apply when the countdown is negative (i.e. when the countdown has reached 0 and is now counting up). This can be a string, an array of strings, or an object with boolean values (like in v-bind:class). These classes will only be applied when the countdown is negative, so you can use this to style the countdown differently when it has reached 0.
         */
        negativeClasses?: string | string[] | Record<string, boolean>;

        /**
         * If we should hide the placeholder when there is no value. By default, it will show '--:--:--' when there is no value. If this is true, it will show an empty string instead.
         * @default false
         */
        hidePlaceholder?: boolean;

        /**
         * Will always show absolute values and never with a negative sign, this is useful if you want to use the component as a count up after reaching 0, but still want to show the time until it reaches 0.
         * So if you have a countdown of 10 seconds, it will show 00:10, 00:09, ..., 00:01, 00:00, 00:01, 00:02, etc. instead of showing -00:01, -00:02, etc.
         * @default false
         */
        useAbsolute?: boolean;
    }>(),
    {
        value: null,
        resolution: 'seconds',
        showNegative: true,
        hideEmptyParts: false,
        hidePlaceholder: false,
        useAbsolute: false,
    },
);

/**
 * This is the internal value of the countdown that we use
 */
const valueInMilliseconds = ref<number | null>(props.value instanceof Date ? props.value.getTime() - new Date().getTime() : props.value);

const element = ref<HTMLElement | null>(null);

let intervalHandle: number | null = null;

/**
 * The emitted events
 */
const emit = defineEmits<{
    /**
     * This is emitted when the countdown reaches 0. If the value is a date, it will emit the date that was reached. If the value is a number, it will emit 0.
     */
    (e: 'countdownReached', reachedTime: Date | number): void;
}>();

watch(
    () => props.value,
    (newValue) => {
        valueInMilliseconds.value = newValue instanceof Date ? newValue.getTime() - new Date().getTime() : newValue;
    },
);

let previousValue: number | null = null;

/**
 * Update the running time display
 *
 */
const updateRunningTime = () => {
    if (element.value) {
        if (valueInMilliseconds.value !== null) {
            const remainingTime = props.showNegative ? valueInMilliseconds.value : Math.max(0, valueInMilliseconds.value);
            const absRemainingTime = Math.abs(remainingTime);
            const hours = Math.floor(absRemainingTime / 3600000);
            const minutes = Math.floor((absRemainingTime % 3600000) / 60000);
            const seconds = Math.floor((absRemainingTime % 60000) / 1000);
            const tenths = Math.floor((absRemainingTime % 1000) / 100);

            if (props.hideEmptyParts) {
                if (hours > 0) {
                    element.value.textContent = `${!props.useAbsolute && remainingTime < 0 ? '-' : ''}${hours.toString().padStart(2, '0')}:${minutes
                        .toString()
                        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}${props.resolution === 'tenths' ? `.${tenths}` : ''}`;
                } else if (minutes > 0) {
                    element.value.textContent = `${!props.useAbsolute && remainingTime < 0 ? '-' : ''}${minutes.toString().padStart(2, '0')}:${seconds
                        .toString()
                        .padStart(2, '0')}${props.resolution === 'tenths' ? `.${tenths}` : ''}`;
                } else {
                    element.value.textContent = `${!props.useAbsolute && remainingTime < 0 ? '-' : ''}${seconds.toString().padStart(2, '0')}${props.resolution === 'tenths' ? `.${tenths}` : ''}`;
                }
            } else {
                element.value.textContent = `${!props.useAbsolute && remainingTime < 0 ? '-' : ''}${hours.toString().padStart(2, '0')}:${minutes
                    .toString()
                    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}${props.resolution === 'tenths' ? `.${tenths}` : ''}`;
            }

            valueInMilliseconds.value = props.resolution == 'seconds' ? valueInMilliseconds.value - 1000 : valueInMilliseconds.value - 100;

            if (valueInMilliseconds.value <= 0 && previousValue == null) {
                emit('countdownReached', props.value instanceof Date ? props.value : 0);

                previousValue = valueInMilliseconds.value;
                valueInMilliseconds.value =
                    props.resolution == 'seconds' ? valueInMilliseconds.value - 1000 : valueInMilliseconds.value - 100;
            }
        } else if (!props.hidePlaceholder) {
            element.value.textContent = '--:--:--';
        }
    }
};

// Start the interval
intervalHandle = window.setInterval(updateRunningTime, props.resolution == 'seconds' ? 1000 : 100);

// Initial update
onMounted(() => {
    updateRunningTime();
});

onUnmounted(() => {
    // Clear the interval
    if (intervalHandle !== null) {
        clearInterval(intervalHandle);
    }
});
</script>
